import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { assessmentApi, staffApi } from '../../services/assessmentApi';
import { saveForm } from '../../services/assessmentStore';
import { newLocalRecord, recordFromServer } from '../../services/assessmentSync';
import { OPEN_STATUSES, StatusBadge, formatDate } from './statusMeta';

const TYPES = [
  { code: 'ia', formType: 'Initial Assessment', label: 'Initial Assessment' },
  { code: '12wr', formType: '12 Week Review', label: '12 Week Review' },
];

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const selectClass = 'mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm '
  + 'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

// Starting a form creates it on this device first and opens it straight away;
// the form page's first sync creates it on the server. Nothing waits on the
// network before the therapist can start typing.
export default function StartAssessmentDialog({ user, existingForms, onClose }) {
  const navigate = useNavigate();
  const privileged = user.role === 'admin' || user.role === 'manager';
  const [code, setCode] = useState('ia');
  const [therapistId, setTherapistId] = useState(String(user.userId));
  const [studentId, setStudentId] = useState('');
  const [date, setDate] = useState(today());
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const therapists = useQuery({
    queryKey: ['assessment-therapists'],
    queryFn: staffApi.therapists,
    enabled: privileged,
  });

  const forSelf = String(therapistId) === String(user.userId);
  const students = useQuery({
    queryKey: ['assessment-students', forSelf ? 'self' : therapistId],
    queryFn: () => (forSelf ? staffApi.myStudents() : staffApi.therapistStudents(therapistId)),
    enabled: !!therapistId,
  });

  const studentList = useMemo(() => [...(students.data?.students || [])]
    .filter((s) => s.isActive !== false)
    .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)),
  [students.data]);

  useEffect(() => { setStudentId(''); setConfirmDuplicate(false); }, [therapistId]);
  useEffect(() => { setConfirmDuplicate(false); }, [studentId, code]);

  const type = TYPES.find((t) => t.code === code);
  const duplicates = (existingForms || []).filter((f) => String(f.studentId) === String(studentId)
    && String(f.therapistId) === String(therapistId)
    && f.formType === type.formType
    && OPEN_STATUSES.includes(f.status));

  const therapistName = privileged
    ? therapists.data?.therapists?.find((t) => String(t.id) === String(therapistId))?.name
    : `${user.firstName || ''} ${user.lastName || ''}`.trim();

  const start = async () => {
    const student = studentList.find((s) => String(s.id) === String(studentId));
    if (!student) return;
    setBusy(true);
    setError(null);
    const record = newLocalRecord({
      formType: type.formType,
      formCode: type.code,
      studentId: student.id,
      studentName: `${student.firstName} ${student.lastName}`.trim(),
      therapistId: Number(therapistId),
      therapistName,
      assessmentDate: date || null,
    });
    try {
      await saveForm(user.userId, record);
      navigate(`/dashboard/assessments/${record.uuid}`);
    } catch {
      // Device storage unavailable: fall back to creating it on the server.
      try {
        const detail = await assessmentApi.create({ clientUuid: record.uuid, ...record.createPayload });
        navigate(`/dashboard/assessments/${recordFromServer(detail).uuid}`);
      } catch (err) {
        setError(err.message);
        setBusy(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4" role="dialog" aria-modal="true">
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Start an assessment</h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <fieldset>
            <legend className="text-sm font-medium text-slate-700">Type</legend>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {TYPES.map((t) => (
                <button key={t.code} type="button" onClick={() => setCode(t.code)} aria-pressed={code === t.code}
                  className={`rounded-lg border px-3 py-2.5 text-sm ${code === t.code
                    ? 'border-blue-600 bg-blue-50 font-medium text-blue-800' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </fieldset>

          {privileged && (
            <label className="block text-sm font-medium text-slate-700">
              Therapist
              <select className={selectClass} value={therapistId} onChange={(e) => setTherapistId(e.target.value)}>
                {(therapists.data?.therapists || []).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}{String(t.id) === String(user.userId) ? ' (me)' : ''}</option>
                ))}
              </select>
              {!forSelf && (
                <span className="mt-1 block text-xs font-normal text-slate-500">
                  It will appear on their list as Assigned.
                </span>
              )}
            </label>
          )}

          <label className="block text-sm font-medium text-slate-700">
            Student
            <select className={selectClass} value={studentId} onChange={(e) => setStudentId(e.target.value)}
              disabled={students.isLoading}>
              <option value="">{students.isLoading ? 'Loading…' : 'Select a student'}</option>
              {studentList.map((s) => (
                <option key={s.id} value={s.id}>{s.lastName}, {s.firstName}</option>
              ))}
            </select>
            {students.isError && (
              <span className="mt-1 block text-xs font-normal text-red-600">
                Could not load students ({students.error.message}).
              </span>
            )}
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Assessment date
            <input type="date" className={`${selectClass} max-w-[12rem]`} value={date} onChange={(e) => setDate(e.target.value)} />
          </label>

          {duplicates.length > 0 && !confirmDuplicate && (
            <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
              <p className="flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-4 w-4" /> There is already an open {type.label} for this student
              </p>
              <ul className="mt-2 space-y-1">
                {duplicates.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center gap-2">
                    <button type="button" className="text-blue-700 underline" onClick={() => navigate(`/dashboard/assessments/${d.id}`)}>
                      Open it
                    </button>
                    <StatusBadge status={d.status} />
                    {d.assessmentDate && <span className="text-xs">{formatDate(d.assessmentDate)}</span>}
                  </li>
                ))}
              </ul>
              <button type="button" className="mt-2 text-xs text-amber-800 underline" onClick={() => setConfirmDuplicate(true)}>
                Start a new one anyway
              </button>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-slate-700 hover:bg-slate-100">
            Cancel
          </button>
          <button type="button" onClick={start}
            disabled={!studentId || busy || (duplicates.length > 0 && !confirmDuplicate)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
            {busy ? 'Starting…' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
}
